/**
 * Inventory Service
 * 
 * Manages product inventory tracking, stock checks, and low stock alerts.
 * Uses in-memory caching with CSV catalog as data source.
 */

import { authFetch } from './apiAuth';
import { INHOUSE_SELLING_ENABLED } from '../config/adminConfig';

// Types
export interface InventoryItem {
  productId: string;
  quantity: number;
  inStock: boolean;
  lastUpdated?: Date | string;
}

export interface InventoryMap {
  [productId: string]: InventoryItem;
}

export interface CartItem {
  id: string;
  quantity: number;
}

export interface StockCheckResult {
  success: boolean;
  available: boolean;
  unavailableItems: CartItem[];
}

export interface ReserveResult {
  success: boolean;
  message?: string;
}

export interface LowStockItem {
  productId: string;
  quantity: number;
  status: 'low' | 'out';
}

export interface AlertResult {
  success: boolean;
  error?: string;
}

// In-memory inventory cache
let cachedInventory: InventoryMap | null = null;

/**
 * Load inventory from CSV catalog (same as main branch)
 * Returns cached version if available
 */
export const loadInventory = async (): Promise<InventoryMap> => {
  // Inventory is only relevant for in-house catalog products;
  // check the flag before cache to ensure consistent gating
  if (!INHOUSE_SELLING_ENABLED) {
    cachedInventory = {};
    return cachedInventory;
  }

  if (cachedInventory) {
    return cachedInventory;
  }

  try {
    // Load from CSV catalog file (matching main branch behavior)
    // TODO: Need to move the dataset to DB and call API in future.
    const inhouseResponse = await fetch('/catalogs/heseda-catalog.csv');
    const kbeautyResponse = await fetch('/catalogs/kbeauty-catalog.csv');
    const inhouseCsvText = await inhouseResponse.text();
    const kbeautyCsvText = await kbeautyResponse.text();

    const lines = inhouseCsvText.split('\n');
    const headers = lines[0].split(',');

    const kbeautyLines = kbeautyCsvText.split('\n');
    const kbeautyHeaders = kbeautyLines[0].split(',');

    const inventory: InventoryMap = {};
    lines.slice(1)
      .filter(line => line.trim())
      .forEach(line => {
        const values = line.split(',');
        const product: Record<string, string> = {};
        headers.forEach((header, index) => {
          product[header.trim()] = values[index]?.trim() || '';
        });

        if (product.id) {
          inventory[product.id] = {
            productId: product.id,
            quantity: parseInt(product.quantityInStock) || 0,
            inStock: product.inStock === 'true'
          };
        }
      });

    kbeautyLines.slice(1)
      .filter(line => line.trim())
      .forEach(line => {
        const values = line.split(',');
        const product: Record<string, string> = {};
        kbeautyHeaders.forEach((header, index) => {
          product[header.trim()] = values[index]?.trim() || '';
        });

        if (product.id) {
          inventory[product.id] = {
            productId: product.id,
            quantity: parseInt(product.quantityInStock) || 0,
            inStock: product.inStock === 'true'
          };
        }
      });

    cachedInventory = inventory;
    return inventory;
  } catch (error) {
    console.error('Error loading inventory:', error);
    // Return empty inventory on error
    cachedInventory = {};
    return cachedInventory;
  }
};

/**
 * Get inventory for specific products
 * @param productIds - Array of product IDs to check
 * @returns Map of product ID to inventory info
 */
export const getInventory = async (productIds: string[]): Promise<InventoryMap> => {
  if (!cachedInventory) {
    await loadInventory();
  }
  
  const result: InventoryMap = {};
  for (const id of productIds) {
    if (cachedInventory && cachedInventory[id]) {
      result[id] = cachedInventory[id];
    } else {
      // Default for unknown products
      result[id] = {
        productId: id,
        quantity: 0,
        inStock: false
      };
    }
  }
  
  return result;
};

/**
 * Check if cart items are available in inventory
 * @param cartItems - Array of cart items with id and quantity
 * @returns Stock check result with availability info
 */
export const checkStock = async (cartItems: CartItem[]): Promise<StockCheckResult> => {
  if (!cachedInventory) {
    await loadInventory();
  }
  
  const unavailableItems: CartItem[] = [];
  
  for (const item of cartItems) {
    const inventory = cachedInventory?.[item.id];
    if (!inventory || inventory.quantity < item.quantity) {
      unavailableItems.push(item);
    }
  }
  
  return {
    success: true,
    available: unavailableItems.length === 0,
    unavailableItems
  };
};

/**
 * Reserve inventory for order
 * Decrements quantities in cache (actual update happens via API on order completion)
 * @param cartItems - Items to reserve
 * @returns Reserve result
 */
export const reserveInventory = async (cartItems: CartItem[]): Promise<ReserveResult> => {
  if (!cachedInventory) {
    await loadInventory();
  }
  
  // First check availability
  const stockCheck = await checkStock(cartItems);
  if (!stockCheck.available) {
    return {
      success: false,
      message: 'Some items are not available in requested quantity'
    };
  }
  
  // Decrement quantities in cache
  for (const item of cartItems) {
    if (cachedInventory && cachedInventory[item.id]) {
      cachedInventory[item.id].quantity -= item.quantity;
      if (cachedInventory[item.id].quantity <= 0) {
        cachedInventory[item.id].inStock = false;
      }
    }
  }
  
  // Check for low stock alerts
  await checkAndAlertLowStock();
  
  return { success: true };
};

/**
 * Update inventory via API
 * @param productId - Product ID to update
 * @param quantity - New quantity
 * @returns Update result
 */
export const updateInventory = async (productId: string, quantity: number): Promise<ReserveResult> => {
  try {
    const response = await authFetch('/api/updateInventory', {
      method: 'POST',
      body: JSON.stringify({
        productId,
        quantity
      })
    });
    
    const result = await response.json();
    
    if (result.success && cachedInventory) {
      // Update cache
      if (!cachedInventory[productId]) {
        cachedInventory[productId] = {
          productId,
          quantity,
          inStock: quantity > 0
        };
      } else {
        cachedInventory[productId].quantity = quantity;
        cachedInventory[productId].inStock = quantity > 0;
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error updating inventory:', error);
    return { success: false, message: 'Failed to update inventory' };
  }
};

/**
 * Check for low stock and send alerts if needed
 */
const checkAndAlertLowStock = async (): Promise<void> => {
  if (!cachedInventory) return;
  
  const lowStockThreshold = 10;
  const lowStockProducts: Array<{ id: string; quantity: number }> = [];
  
  for (const [productId, inventory] of Object.entries(cachedInventory)) {
    if (inventory.quantity > 0 && inventory.quantity < lowStockThreshold) {
      lowStockProducts.push({
        id: productId,
        quantity: inventory.quantity
      });
    }
  }
  
  if (lowStockProducts.length > 0) {
    await sendLowStockAlert(lowStockProducts);
  }
};

/**
 * Send low stock alert to admin
 * @param products - Array of low stock products
 * @returns Alert result
 */
const sendLowStockAlert = async (products: Array<{ id: string; quantity: number }>): Promise<AlertResult> => {
  try {
    const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'dev@heseda.com';
    
    const response = await authFetch('/api/sendLowStockAlert', {
      method: 'POST',
      body: JSON.stringify({
        adminEmail,
        products
      })
    });
    
    const result: AlertResult = await response.json();
    
    if (result.success) {
      console.log(`📧 Low stock alert sent to ${adminEmail} for ${products.length} products`);
    } else {
      console.error('Low stock alert failed:', result.error);
    }
    
    return result;
  } catch (error) {
    console.error('Error sending low stock alert:', error);
    throw error;
  }
};

/**
 * Release inventory (restore quantities - for cancelled orders)
 * @param items - Items to release
 * @returns Release result
 */
export const releaseInventory = async (items: CartItem[]): Promise<ReserveResult> => {
  if (!cachedInventory) {
    await loadInventory();
  }
  
  for (const item of items) {
    if (cachedInventory && cachedInventory[item.id]) {
      cachedInventory[item.id].quantity += item.quantity;
      cachedInventory[item.id].inStock = true;
    }
  }
  
  return { success: true };
};

/**
 * Get low stock alerts
 * @param threshold - Quantity threshold for low stock (default: 10)
 * @returns Array of low stock items
 */
export const getLowStockAlerts = async (threshold: number = 10): Promise<LowStockItem[]> => {
  if (!cachedInventory) {
    await loadInventory();
  }
  
  const lowStock: LowStockItem[] = [];
  
  if (!cachedInventory) return lowStock;
  
  for (const [productId, inventory] of Object.entries(cachedInventory)) {
    if (inventory.quantity > 0 && inventory.quantity < threshold) {
      lowStock.push({
        productId,
        quantity: inventory.quantity,
        status: 'low'
      });
    } else if (inventory.quantity === 0) {
      lowStock.push({
        productId,
        quantity: 0,
        status: 'out'
      });
    }
  }
  
  return lowStock;
};

/**
 * Refresh inventory cache
 * Forces reload from API
 * @returns Fresh inventory map
 */
export const refreshInventory = async (): Promise<InventoryMap> => {
  cachedInventory = null;
  return await loadInventory();
};

/**
 * Clear inventory cache
 * Useful for testing or forcing fresh load
 */
export const clearInventoryCache = (): void => {
  cachedInventory = null;
};

/**
 * Alias for checkStock for backwards compatibility
 * @param cartItems - Array of cart items with id and quantity
 * @returns Stock check result with availability info
 */
export const checkAvailability = checkStock;
